import { supportReturnPath, adminSupportReturnPath } from "./lib/supportNavigation";
import { profileKey } from "./lib/profileSession";
import { CompanyVerificationNotice } from "./components/CompanyVerificationNotice";
import { Router, Route, Switch, Redirect, useLocation, useSearch } from "wouter";
import { useSafeBrowserLocation } from "./lib/router-safe";
import { BreadcrumbTrailProvider } from "./lib/breadcrumbTrail";
import { lazy, Suspense, useEffect, useRef } from "react";
import { PostLifecycleNotice } from "./components/PostLifecycleNotice";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { SupportChatWidget } from "./components/SupportChatWidget"; // [#11]
import { BottomNav } from "./components/BottomNav";
import { VerifyBanner } from "./components/VerifyBanner";
import { IdentityReviewBanner } from "./components/IdentityReviewBanner";
import { useAuth } from "./lib/auth";
import { useDesktopAdminAccess } from "./lib/clientSurface";
import { analyticsFeatureForPath, browserAnalyticsOptedOut, emitAnalyticsFeatureAction, emitAnalyticsPageView, isAnalyticsPageViewPathEligible, isForegroundBrowserDocument } from "./lib/analytics";
import { trackGooglePageView } from "./lib/googleAnalytics";
import type { AnalyticsFeature } from "./lib/api";
import Home from "./pages/Home";
import Marketplace from "./pages/Marketplace";
import ResumeDetail from "./pages/ResumeDetail";
import ResumeViewer from "./pages/ResumeViewer";
import CompanyDetail from "./pages/CompanyDetail";
import ServiceDetail from "./pages/ServiceDetail";
import JobDetail from "./pages/JobDetail";
import ProjectDetail from "./pages/ProjectDetail";
import RfqDetail from "./pages/RfqDetail";
import ItemDetail from "./pages/ItemDetail";
import OrderDetail from "./pages/OrderDetail";
import EngagementDetail from "./pages/EngagementDetail";
import Profile from "./pages/Profile";
import AccountProfile from "./pages/AccountProfile";
import CompanySettings from "./pages/CompanySettings";
import CompanyWizard from "./pages/CompanyWizard";
import CompanyLicenses from "./pages/CompanyLicenses";
import CompanyDashboard from "./pages/CompanyDashboard";
import AdminVerifications from "./pages/AdminVerifications";
import PostFlow from "./pages/PostFlow";
import Signup from "./pages/Signup";
import ChooseActivities from "./pages/ChooseActivities";
import Welcome from "./pages/Welcome";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Messages from "./pages/Messages";
import DisputeMessages from "./pages/DisputeMessages";
import Wallet from "./pages/Wallet";
import Contracts from "./pages/Contracts";
import QuotationActivity from "./pages/QuotationActivity";
import Settings from "./pages/Settings";
import AccountProfilePrivacy from "./pages/AccountProfilePrivacy";
import Saved from "./pages/Saved";
import Search from "./pages/Search";
import Verify from "./pages/Verify";
import WorkerVerification from "./pages/WorkerVerification";
import WorkerProfile from "./pages/WorkerProfile";
import HowItWorks from "./pages/HowItWorks";
import PricingAvailability from "./pages/PricingAvailability";
import Billing from "./pages/Billing";
import LegalDoc from "./pages/LegalDoc";
import LegalHub from "./pages/LegalHub";
import Changelog from "./pages/Changelog";
import SettlementTerms from "./pages/SettlementTerms";
import LegalGate from "./components/LegalGate";
import Support from "./pages/Support";
import Safety from "./pages/Safety";
import Earnings from "./pages/Earnings";
import Blogs from "./pages/Blogs";
import BlogPost from "./pages/BlogPost";
import BlogAvailability from "./pages/BlogAvailability";
const Admin = lazy(() => import("./pages/Admin"));

const MOBILE_BOTTOM_NAV_ROUTES = new Set(["/", "/marketplace", "/messages", "/messages/disputes", "/dashboard"]);

function ExploreRedirect({ tab }: { tab: string }) {
  const search = useSearch();
  const [, navigate] = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    params.set("tab", tab);
    navigate(`/marketplace?${params.toString()}`, { replace: true });
  }, [navigate, search, tab]);

  return null;
}

function ScrollTop() {
  const [loc] = useLocation();
  // NOTE: must NOT implicitly return window.scrollTo(...)'s value — if an
  // extension patches scrollTo to return non-undefined, React treats it as the
  // effect cleanup and later calls it → "c is not a function" on navigation.
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ignore */
    }
  }, [loc]);
  return null;
}

function Shell() {
  const [loc, navigate] = useLocation();
  const search = useSearch();
  const { user, loading: authLoading } = useAuth();
  const isAdmin = user?.userType === "admin";
  const desktopAdminAccess = useDesktopAdminAccess();
  // Support remains reachable for setup problems, including the sign-in handoff.
  const supportEntry = loc === "/support" || (loc === "/login" && !!supportReturnPath(new URLSearchParams(search).get("returnTo")));
  const needsActivities = !authLoading && !!user && !isAdmin && user.capabilities.length === 0 && !supportEntry && loc !== "/changelog" && loc !== "/legal" && !loc.startsWith("/legal/");
  const bare = needsActivities || loc === "/login" || loc === "/signup" || loc === "/choose-activities";
  const isMessagesFeature = loc === "/messages" || loc.startsWith("/messages/");
  // [messaging.responsive-inbox](../../docs/product/messaging-and-notifications.md#messaging-responsive-inbox): a new-message composer needs the same clear bottom edge as an
  // open conversation; bottom tabs remain available on the inbox.
  const showMobileBottomNav = MOBILE_BOTTOM_NAV_ROUTES.has(loc)
    && !(loc === "/messages" && new URLSearchParams(search).get("to"));
  const showMobileSupportChat = loc === "/" || loc === "/marketplace"
    || (loc === "/dashboard" && !new URLSearchParams(search).get("view"));
  const lastAnalyticsLocation = useRef<string | null>(null);
  const lastGoogleLocation = useRef<string | null>(null);
  const pendingAnalyticsLocations = useRef<string[]>([]);
  const pendingFeatureNavigations = useRef<AnalyticsFeature[]>([]);

  // Zero saved activities resumes setup outside the public-reference and Support exceptions above.
  useEffect(() => {
    if (needsActivities && loc !== "/choose-activities") navigate("/choose-activities", { replace: true });
  }, [needsActivities, loc, navigate]);

  useEffect(() => {
    if (lastAnalyticsLocation.current !== loc) {
      lastAnalyticsLocation.current = loc;
      if (isForegroundBrowserDocument()) {
        if (isAnalyticsPageViewPathEligible(loc)) pendingAnalyticsLocations.current.push(loc);
        const feature = analyticsFeatureForPath(loc);
        if (feature) pendingFeatureNavigations.current.push(feature);
      }
    }

    if (authLoading) return;
    const eligibleLocations = pendingAnalyticsLocations.current;
    pendingAnalyticsLocations.current = [];
    const featureNavigations = pendingFeatureNavigations.current;
    pendingFeatureNavigations.current = [];
    if (isAdmin) {
      trackGooglePageView(loc, false);
      return;
    }
    for (const path of eligibleLocations) {
      void emitAnalyticsPageView(path);
    }
    if (lastGoogleLocation.current !== loc) {
      lastGoogleLocation.current = loc;
      trackGooglePageView(loc, isForegroundBrowserDocument() && isAnalyticsPageViewPathEligible(loc) && !browserAnalyticsOptedOut());
    }
    if (!user) return;
    for (const feature of featureNavigations) void emitAnalyticsFeatureAction(feature);
  }, [loc, authLoading, isAdmin, user]);

  return (
    <div className={isMessagesFeature ? "flex h-[100dvh] min-h-0 flex-col overflow-hidden [&>header]:shrink-0" : "min-h-full flex flex-col overflow-x-clip"}>
      <ScrollTop />
      {!bare && <Header />}
      <PostLifecycleNotice />
      {!bare && <VerifyBanner />}
      {!bare && <IdentityReviewBanner />}
      {!bare && <CompanyVerificationNotice />}
      <main className={isMessagesFeature ? "min-h-0 flex-1" : "flex-1"}>
        {needsActivities ? <ChooseActivities /> : <Switch>
          <Route path="/" component={Home} />
          <Route path="/marketplace">{() => <Marketplace />}</Route>
          <Route path="/jobseekers">{() => <ExploreRedirect tab="Jobseekers" />}</Route>
          <Route path="/workers">{() => <ExploreRedirect tab="Jobseekers" />}</Route>
          <Route path="/services">{() => <ExploreRedirect tab="Services" />}</Route>
          <Route path="/companies">{() => <ExploreRedirect tab="Companies" />}</Route>
          <Route path="/jobs">{() => <ExploreRedirect tab="Jobs" />}</Route>
          <Route path="/projects">{() => <ExploreRedirect tab="Project Bidding" />}</Route>
          <Route path="/p/:id/resume" component={ResumeViewer} />
          <Route path="/p/:id" component={ResumeDetail} />
          <Route path="/c/:id" component={CompanyDetail} />
          <Route path="/s/:id" component={ServiceDetail} />
          <Route path="/j/:id" component={JobDetail} />
          <Route path="/project/:id" component={ProjectDetail} />
          <Route path="/rfq/:id" component={RfqDetail} />
          <Route path="/item/:id" component={ItemDetail} />
          <Route path="/orders/:id" component={OrderDetail} />
          <Route path="/engagement/:id" component={EngagementDetail} />
          <Route path="/u/:id" component={Profile} />
          <Route path="/company/new" component={CompanyWizard} />
          <Route path="/company/:id/edit" component={CompanyWizard} />
          <Route path="/company/:id/licenses" component={CompanyLicenses} />
          <Route path="/company/:id/dashboard" component={CompanyDashboard} />
          <Route path="/company/:id/manage" component={CompanySettings} />
          <Route path="/admin">{() => desktopAdminAccess ? <Suspense fallback={<div className="container-x py-12 text-sm text-mut">Loading Admin portal…</div>}><Admin /></Suspense> : <Redirect to={adminSupportReturnPath(`/admin?${search}`) ? `/login?returnTo=${encodeURIComponent(`/admin?${search}`)}` : "/login?error=signin"} replace />}</Route>
          <Route path="/admin/verifications">{() => desktopAdminAccess ? <AdminVerifications /> : <Redirect to="/login?error=signin" replace />}</Route>
          <Route path="/post/company" component={CompanyWizard} />
          <Route path="/post/:type" component={PostFlow} />
          <Route path="/how-it-works" component={HowItWorks} />
          <Route path="/pricing" component={PricingAvailability} />
          <Route path="/blogs"><BlogAvailability key={loc}><Blogs /></BlogAvailability></Route>
          <Route path="/blogs/:slug"><BlogAvailability key={loc}><BlogPost /></BlogAvailability></Route>
          <Route path="/billing" component={Billing} />
          <Route path="/legal/settlement-terms" component={SettlementTerms} />
          <Route path="/legal" component={LegalHub} />
          <Route path="/changelog" component={Changelog} />
          <Route path="/legal/:kind" component={LegalDoc} />
          <Route path="/support" component={Support} />
          <Route path="/safety" component={Safety} />
          <Route path="/earnings" component={Earnings} />
          <Route path="/signup" component={Signup} />
          <Route path="/choose-activities" component={ChooseActivities} />
          <Route path="/login" component={Login} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/welcome" component={Welcome} />
          <Route path="/dashboard" component={Dashboard} />
          <Route path="/messages/disputes/:id" component={DisputeMessages} />
          <Route path="/messages/disputes" component={DisputeMessages} />
          <Route path="/messages/:threadId" component={Messages} />
          <Route path="/messages" component={Messages} />
          <Route path="/wallet" component={Wallet} />
          <Route path="/contracts" component={Contracts} />
          <Route path="/quotations/:id/activity" component={QuotationActivity} />
          <Route path="/settings/profile" component={AccountProfile} />
          <Route path="/settings/profile-privacy" component={AccountProfilePrivacy} />
          <Route path="/settings" component={Settings} />
          <Route path="/saved" component={Saved} />
          <Route path="/invite">
            <div className="container-x py-24 text-center">
              <h1 className="text-2xl font-bold text-navy">Referrals are temporarily unavailable</h1>
              <p className="mt-3 text-mut">The referral programme is paused while we complete the experience.</p>
            </div>
          </Route>
          <Route path="/search" component={Search} />
          <Route path="/verify" component={Verify} />
          <Route path="/get-verified" component={WorkerVerification} />
          <Route path="/worker-profile" component={WorkerProfile} />
          <Route>
            <div className="container-x py-24 text-center">
              <h1 className="text-3xl font-extrabold text-navy">404</h1>
              <p className="text-mut mt-2">Page not found.</p>
            </div>
          </Route>
        </Switch>}
      </main>
      {!bare && !isAdmin && !isMessagesFeature && <Footer />}
      {!bare && !isAdmin && !isMessagesFeature && (
        <div className={showMobileSupportChat ? "" : "max-lg:hidden"}><SupportChatWidget /></div>
      )}
      {!bare && !isAdmin && showMobileBottomNav && <BottomNav />}
      {!bare && <LegalGate />}
    </div>
  );
}

export default function App() {
  const { user } = useAuth();
  // Crash-proof clean-URL routing (see lib/router-safe): guards every browser API
  // in try/catch and drives navigation via React state, so page-injecting browser
  // extensions that break addEventListener/pushState can't white-screen the app.
  return (
    <Router hook={useSafeBrowserLocation}>
      {/* Discard page drafts, selections, badge state and pending view updates on a profile switch. */}
      <BreadcrumbTrailProvider key={profileKey(user)}><Shell /></BreadcrumbTrailProvider>
    </Router>
  );
}
